import { Box, Button, Em, Heading, IconButton, Popover, Tabs, Text, TextField, Tooltip } from '@radix-ui/themes';
import { nanoid } from '@reduxjs/toolkit';
import HubertCheckbox from 'components/HubertUI/HubertCheckbox';
import { useQuestion } from 'hooks/useQuestion';
import { useQuestionRules } from 'hooks/useQuestionRules';
import { BadgeCheck, CircleX, Info, Star, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { EEvaluationType, TEvaluation } from 'types/evaluation.types';
import { EConditionType, ERuleType, type TQuestionRule, type TQuestionRuleAction } from 'types/rules.types';
import { ENodeType, type TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import { DEFAULT_EARLY_REJECT_QUESTION_ID } from '../constants';
import { findAutoEarlyRejectRule } from './findAutoEarlyRejectRule';
import ResetEvaluationBanner from './ResetEvaluationBanner';

type Tab = 'info' | 'warning' | 'bonus' | 'point' | 'disqualified' | 'auto_qualified';

// Carries the early-reject rule up to the caller alongside the evaluation change, so a single
// combined apply-automatic-knockout request can persist both — see EarlyReject type below for why
// this can't just be dispatched from here directly.
export type EarlyReject = { rule: TQuestionRule; addQuestionIds: string[] };

type Props = {
  allowedTabs: Tab[];
  templateId?: string;
  nodeId: string;
  customKey: string;
  evaluation?: TEvaluation;
  isDefault: boolean;
  // The caller (not this component) dispatches — it's the one with option index/language/mode
  // context needed to also update the local editor copy, same as every other evaluation change.
  onEvaluationChange: (e: TEvaluation, earlyReject?: EarlyReject, removeEarlyRejectRuleId?: string) => void;
  answerType: 'option' | 'intent' | 'number' | 'matrix' | 'open-weighted' | 'slider';
};

const EvaluationPopover = ({
  allowedTabs,
  templateId,
  nodeId,
  customKey,
  evaluation,
  isDefault,
  onEvaluationChange,
}: Props) => {
  const { t, i18n } = useTranslation('Templates');

  const [tabValue, setTabValue] = useState<string>('');
  const [bonusPoints, setBonusPoints] = useState<number>(0);
  const [createEarlyReject, setCreateEarlyReject] = useState<boolean>(false);

  const { list: effectiveRules } = useQuestionRules(nodeId);
  const existingEarlyRejectRule = useMemo(
    () => findAutoEarlyRejectRule(effectiveRules, customKey),
    [effectiveRules, customKey],
  );

  const earlyRejectQuestion = useQuestion(DEFAULT_EARLY_REJECT_QUESTION_ID);
  // Purely a preview of what this rule jumps to — match the recruiter's own UI language (already
  // what every label around it is in) rather than the template's language, since the two have no
  // reason to line up and a mismatch just reads as broken. i18n's language codes are short ('sv'),
  // the question's are region-specific ('sv-se'), so match by prefix rather than exact equality.
  const earlyRejectPreviewLanguage: TAvailableLanguageCodes =
    earlyRejectQuestion.question?.available_languages.find((lang) => lang.startsWith(i18n.language)) ??
    earlyRejectQuestion.language ??
    'en-gb';
  const earlyRejectPreviewHtml = earlyRejectQuestion.question?.text?.[earlyRejectPreviewLanguage]?.join(' ') ?? '';

  const tabs = useMemo<{ id: Tab; name: string; description: string }[]>(
    () => [
      { id: 'info', name: t('evaluationPopover.tabs.informational'), description: '' },
      { id: 'warning', name: t('evaluationPopover.tabs.warning'), description: '' },
      { id: 'bonus', name: t('evaluationPopover.tabs.bonusPoints'), description: '' },
      { id: 'point', name: t('evaluationPopover.tabs.points'), description: '' },
      { id: 'disqualified', name: t('evaluationPopover.tabs.disqualified'), description: '' },
      { id: 'auto_qualified', name: t('evaluationPopover.tabs.autoQualify'), description: '' },
    ],
    [t],
  );

  useEffect(() => {
    setTabValue(evaluation?.name ?? '');
    setBonusPoints(evaluation?.bonus_points ?? 0);
    setCreateEarlyReject(!!existingEarlyRejectRule);
  }, [evaluation, existingEarlyRejectRule]);

  if (!evaluation || !evaluation?.name) {
    return <span>{t('evaluationPopover.noEvaluation')}</span>;
  }

  return (
    <Popover.Root>
      <Popover.Trigger>
        <IconButton className="min-w-12 bg-transparent" style={{ backgroundColor: 'transparent' }}>
          {/* TODO: Why variant overriding bg-transparent? */}
          {(() => {
            if (evaluation?.name === 'info') {
              return (
                <Tooltip content={t('evaluationPopover.iconText.info')}>
                  <Info className="text-info cursor-pointer" />
                </Tooltip>
              );
            } else if (evaluation?.name === 'warning') {
              return (
                <Tooltip content={t('evaluationPopover.iconText.warning')}>
                  <TriangleAlert className="text-warning cursor-pointer" />
                </Tooltip>
              );
            } else if (evaluation?.name === 'disqualified') {
              return (
                <Tooltip content={t('evaluationPopover.iconText.disqualified')}>
                  <CircleX className="text-danger cursor-pointer" />
                </Tooltip>
              );
            } else if (evaluation?.name === 'auto_qualified') {
              return (
                <Tooltip content={t('evaluationPopover.iconText.autoQualified')}>
                  <BadgeCheck className="text-evaluation-qualified cursor-pointer" />
                </Tooltip>
              );
            } else if (evaluation?.name === 'bonus') {
              return (
                // TODO(i18n): Category 4 — template literal mixes interpolation with enum text; restructure before extracting
                <Tooltip
                  content={`This option results in ${evaluation?.bonus_points} bonus points. ${t('evaluationPopover.iconText.bonus')}`}
                >
                  <div className="text-success flex cursor-pointer font-semibold">
                    <Star />
                    <span>{evaluation?.bonus_points}</span>
                  </div>
                </Tooltip>
              );
            }
          })()}
        </IconButton>
      </Popover.Trigger>

      <Popover.Content>
        <Tabs.Root
          defaultValue={tabValue}
          value={tabValue}
          onValueChange={(e) => {
            setTabValue(e);
          }}
          activationMode="manual"
        >
          <Tabs.List size="1">
            {tabs.map((tab) => {
              if (!allowedTabs.includes(tab.id)) {
                return null;
              }

              return (
                <Tabs.Trigger key={`evaluation-popover-tab-trigger-${tab.id}`} value={tab.id}>
                  {tab.name}
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>

          <Box px="4" pt="4" pb="4">
            {tabs.map((tab) => {
              if (!allowedTabs.includes(tab.id)) {
                return null;
              }

              if (tab.id === 'bonus') {
                return (
                  <Tabs.Content
                    key={`evaluation-popover-tab-content-${tab.id}`}
                    value={tab.id}
                    className="min-h-50 min-w-75"
                  >
                    <Heading size="3" weight={'medium'}>
                      {tab.name}
                    </Heading>

                    <ResetEvaluationBanner
                      templateId={templateId}
                      nodeId={nodeId}
                      customKey={customKey}
                      isDefault={isDefault}
                    />

                    <Text as="p" size="2">
                      {t('evaluationPopover.iconText.bonus')}
                    </Text>
                    <Text size="1" color="violet">
                      <Em>{t('evaluationPopover.bonusPointsHint')}</Em>
                    </Text>
                    <TextField.Root
                      defaultValue={bonusPoints}
                      type="number"
                      min={0}
                      max={100}
                      onBlur={(e) => {
                        const valueAsNumber = e.target.valueAsNumber;
                        const min = parseInt(e.target.min);
                        const max = parseInt(e.target.max);
                        if (!isNaN(valueAsNumber) && valueAsNumber >= min && valueAsNumber <= max)
                          setBonusPoints(e.target.valueAsNumber);
                      }}
                      onFocus={(e) => e.target.select()}
                    />
                  </Tabs.Content>
                );
              }

              return (
                <Tabs.Content
                  value={tab.id}
                  key={`evaluation-popover-tab-content-${tab.id}`}
                  className="min-h-50 min-w-75"
                >
                  <Heading size="3" weight={'medium'}>
                    {tab.name}
                  </Heading>

                  <ResetEvaluationBanner
                    templateId={templateId}
                    nodeId={nodeId}
                    customKey={customKey}
                    isDefault={isDefault}
                  />

                  <Text size="1">
                    {(() => {
                      if (tab.id === 'info') {
                        return <div>{t('evaluationPopover.iconText.info')}</div>;
                      }
                      if (tab.id === 'warning') {
                        return <div>{t('evaluationPopover.iconText.warning')}</div>;
                      }
                      if (tab.id === 'disqualified') {
                        return (
                          <div className="flex flex-col gap-2">
                            <div>{t('evaluationPopover.iconText.disqualified')}</div>

                            <label className="flex items-center gap-2 text-sm">
                              <HubertCheckbox
                                checked={createEarlyReject}
                                disabled={!!existingEarlyRejectRule}
                                onCheckedChange={(checked) => setCreateEarlyReject(checked === true)}
                              />
                              <span>{t('evaluationPopover.earlyReject.checkboxLabel')}</span>
                            </label>

                            {/* Kept mounted (just hidden) rather than conditionally rendered — toggling
                                this in and out of the layout changes the popover's height mid-
                                interaction, which makes Radix's collision-avoidance logic reposition
                                the whole popover as if it just opened somewhere else. */}
                            <div
                              className={
                                createEarlyReject
                                  ? 'flex flex-col gap-1'
                                  : 'pointer-events-none invisible flex flex-col gap-1'
                              }
                              aria-hidden={!createEarlyReject}
                            >
                              <Text size="1" color="gray">
                                {t('evaluationPopover.earlyReject.targetLabel')}
                              </Text>

                              <div
                                className="rounded-md border border-solid border-neutral-200 bg-neutral-50 p-2 text-sm"
                                dir="auto"
                                dangerouslySetInnerHTML={{
                                  __html: earlyRejectPreviewHtml || DEFAULT_EARLY_REJECT_QUESTION_ID,
                                }}
                              />

                              <Text size="1" color="gray">
                                {t('evaluationPopover.earlyReject.editHint')}
                              </Text>
                            </div>
                          </div>
                        );
                      }
                      if (tab.id === 'auto_qualified') {
                        return <div>{t('evaluationPopover.iconText.autoQualified')}</div>;
                      }
                    })()}
                  </Text>
                </Tabs.Content>
              );
            })}
          </Box>

          <div className="flew-row flex items-center justify-between gap-4">
            {/* TODO(i18n): Category 4 — sentence split across JSX children with inline <Link>; restructure before extracting */}
            <Text size="1" weight={'medium'}>
              Read more about Hubert&apos;s evaluation process{' '}
              <Link to={'/evaluation'} target="_blank" className="text-info-500">
                here
              </Link>
              .
            </Text>

            <Popover.Close>
              <Button
                size="1"
                className="bg-primary text-secondary cursor-pointer"
                onClick={() => {
                  const e: TEvaluation = {
                    id: tabValue,
                    name: tabValue as EEvaluationType,
                    isDefault: false,
                  };

                  if (tabValue === 'bonus') e.bonus_points = bonusPoints;
                  if (tabValue === 'points') {
                    e.points = -1;
                    e.weighted = true;
                  }

                  let earlyReject: EarlyReject | undefined;
                  if (tabValue === 'disqualified' && createEarlyReject && !existingEarlyRejectRule) {
                    const rule: TQuestionRule = {
                      id: nanoid(),
                      name: 'Early reject',
                      type: ERuleType.DO_IF,
                      operator: 'AND',
                      conditions: [
                        {
                          id: nanoid(),
                          name: '',
                          type: EConditionType.QuestionAnswer,
                          operator: 'EQUALS',
                          value: customKey,
                        },
                      ],
                      actions: [
                        {
                          id: nanoid(),
                          name: 'JUMP_TO',
                          value: [
                            {
                              type: ENodeType.Question,
                              id: DEFAULT_EARLY_REJECT_QUESTION_ID,
                              version: earlyRejectQuestion.version ?? 0,
                            },
                          ] as unknown as TQuestionRuleAction['value'],
                        },
                      ],
                    };
                    // Outline/addQuestionIds is a template-scoped concept — outside a template
                    // there's no outline to add the bridge question to.
                    earlyReject = { rule, addQuestionIds: templateId ? [DEFAULT_EARLY_REJECT_QUESTION_ID] : [] };
                  }

                  // Independent of createEarlyReject — that checkbox only governs adding a new rule.
                  const removeEarlyRejectRuleId =
                    tabValue !== 'disqualified' && existingEarlyRejectRule ? existingEarlyRejectRule.id : undefined;

                  // name/bonus_points are the only fields that vary with user input here — skip the
                  // call (and whatever save it triggers) when neither changed and there's no rule to
                  // attach or remove (an early-reject rule can be added onto an evaluation that was
                  // already disqualified, so that alone must still trigger the call).
                  const evaluationUnchanged = e.name === evaluation.name && e.bonus_points === evaluation.bonus_points;
                  if (onEvaluationChange && (!evaluationUnchanged || earlyReject || removeEarlyRejectRuleId)) {
                    onEvaluationChange(e, earlyReject, removeEarlyRejectRuleId);
                  }
                }}
              >
                {t('evaluationPopover.doneButton')}
              </Button>
            </Popover.Close>
          </div>
        </Tabs.Root>
      </Popover.Content>
    </Popover.Root>
  );
};

export default EvaluationPopover;
